/** Signed-out screens: sign in, forgot password, reset password (emailed link), accept an invitation. */

import { useEffect, useState } from "react";
import { api, type User } from "./api";

const MIN_PASSWORD = 10;

function useForm(run: () => Promise<void>) {
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await run();
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return { err, setErr, busy, submit };
}

export function Login({ onDone }: { onDone: (u: User) => void }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const f = useForm(async () => onDone((await api.login(email, pw)).user));
  return (
    <div className="login">
      <form onSubmit={f.submit}>
        <h1>Whiteboard Studio</h1>
        <input type="email" autoFocus autoComplete="username" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input type="password" autoComplete="current-password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} />
        <button className="primary" disabled={f.busy || !email || !pw}>Sign in</button>
        {f.err && <p className="bad">{f.err}</p>}
        <a className="small centered" href="#/forgot">Forgot your password?</a>
        <p className="muted small centered">Accounts are by invitation. Ask the admin for one.</p>
      </form>
    </div>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const f = useForm(async () => {
    await api.forgot(email);
    setSent(true);
  });
  return (
    <div className="login">
      <form onSubmit={f.submit}>
        <h1>Reset your password</h1>
        {sent ? (
          <p className="small">
            If <strong>{email}</strong> has an account, a reset link is on its way. It works once, for an hour. Check your spam folder if it doesn't
            arrive in a few minutes.
          </p>
        ) : (
          <>
            <p className="muted small">Enter your account's email and we'll send you a link to choose a new password.</p>
            <input type="email" autoFocus autoComplete="username" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <button className="primary" disabled={f.busy || !email}>Send reset link</button>
          </>
        )}
        {f.err && <p className="bad">{f.err}</p>}
        <a className="small centered" href="#/">Back to sign in</a>
      </form>
    </div>
  );
}

/** New password twice, with the length rule checked before submitting. */
export function NewPassword({ pw, setPw, again, setAgain }: { pw: string; setPw: (s: string) => void; again: string; setAgain: (s: string) => void }) {
  return (
    <>
      <input type="password" autoComplete="new-password" placeholder={`New password (${MIN_PASSWORD}+ characters)`} value={pw} onChange={(e) => setPw(e.target.value)} />
      <input type="password" autoComplete="new-password" placeholder="Repeat it" value={again} onChange={(e) => setAgain(e.target.value)} />
      {again && pw !== again && <p className="bad small">The passwords don't match.</p>}
    </>
  );
}

export const passwordOk = (pw: string, again: string) => pw.length >= MIN_PASSWORD && pw === again;

export function Reset({ token, onDone }: { token: string; onDone: () => void }) {
  const [pw, setPw] = useState("");
  const [again, setAgain] = useState("");
  const f = useForm(async () => {
    await api.resetPassword(token, pw);
    onDone();
  });
  return (
    <div className="login">
      <form onSubmit={f.submit}>
        <h1>Choose a new password</h1>
        <NewPassword pw={pw} setPw={setPw} again={again} setAgain={setAgain} />
        <button className="primary" disabled={f.busy || !passwordOk(pw, again)}>Save and sign in</button>
        {f.err && <p className="bad">{f.err}</p>}
        <a className="small centered" href="#/forgot">Send a new link</a>
      </form>
    </div>
  );
}

export function AcceptInvite({ token, onDone }: { token: string; onDone: (u: User) => void }) {
  const [email, setEmail] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [again, setAgain] = useState("");
  const f = useForm(async () => onDone((await api.inviteAccept(token, name, pw)).user));
  useEffect(() => {
    api.inviteCheck(token).then((r) => setEmail(r.email)).catch((e) => f.setErr(e.message));
  }, [token]);
  return (
    <div className="login">
      <form onSubmit={f.submit}>
        <h1>Create your account</h1>
        {email && (
          <>
            <p className="muted small">You were invited as <strong>{email}</strong>.</p>
            <input type="email" autoComplete="username" value={email} readOnly hidden />
            <input autoFocus autoComplete="name" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
            <NewPassword pw={pw} setPw={setPw} again={again} setAgain={setAgain} />
            <button className="primary" disabled={f.busy || !name.trim() || !passwordOk(pw, again)}>Create account</button>
          </>
        )}
        {f.err && <p className="bad">{f.err}</p>}
        <a className="small centered" href="#/">Sign in instead</a>
      </form>
    </div>
  );
}
